import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from "@/components/ui/use-toast";
import { SchemaOrg } from '@/components/utils/SchemaOrg';
import NotFoundPage from '@/pages/NotFoundPage';
import { getBlogPostBySlug, allBlogPostsObject } from '@/data/blogData';
import PostHeaderContent from '@/components/blog/PostHeaderContent';
import PostFeaturedImage from '@/components/blog/PostFeaturedImage';
import PostContentRenderer from '@/components/blog/PostContentRenderer';
import PostInteractionBar from '@/components/blog/PostInteractionBar';
import RelatedArticles from '@/components/blog/RelatedArticles';
import CommentSubmissionForm from '@/components/blog/CommentSubmissionForm';

const BlogPostPage = ({ onActionClick }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const routeLocation = useLocation(); 
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      const foundPost = getBlogPostBySlug(slug);
      setPost(foundPost);
      setIsLoading(false);
    }, 300);
  }, [slug]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mb-4"></div>
      </div>
    );
  }

  if (!post) {
    return <NotFoundPage />;
  }

  const pageTitle = `${post.title} | WebFiny Blog`;
  const pageDescription = post.excerpt;
  const canonicalUrl = `${window.location.origin}${routeLocation.pathname}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image,
    "author": {
      "@type": "Person",
      "name": post.author,
      "description": post.authorRole,
      "image": post.authorImage
    },
    "publisher": {
      "@type": "Organization",
      "name": "WebFiny",
      "logo": {
        "@type": "ImageObject",
        "url": "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png"
      }
    },
    "datePublished": post.date,
    "dateModified": post.date, 
    "articleBody": post.content.replace(/<[^>]*>?/gm, '').substring(0, 1000) + "...", 
    "keywords": post.tags.join(", ")
  };

  const relatedPosts = Object.values(allBlogPostsObject)
    .filter(relatedPost => relatedPost.slug !== slug && relatedPost.category === post.category)
    .slice(0, 2);

  return (
    <>
      <SchemaOrg
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schemas={[articleSchema]}
      />
      <div className="bg-gradient-to-b from-background to-muted/20 py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Button variant="outline" onClick={() => navigate('/blog')} className="mb-8 group hover:bg-accent/50 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Blog
            </Button>

            <article>
              <PostHeaderContent post={post} />
              <PostFeaturedImage image={post.image} title={post.title} />
              <PostContentRenderer content={post.content} />
              <PostInteractionBar post={post} onActionClick={onActionClick} toast={toast} />
            </article>
            
            <RelatedArticles posts={relatedPosts} />
            <CommentSubmissionForm onActionClick={onActionClick} />

          </motion.div>
        </div>
      </div>
    </>
  );
};

export default BlogPostPage;